<script setup lang="ts">
import Customer from "@/customer/interfaces/Customer";
import useCustomerConversation from "@/customer/composables/conversation/useCustomerConversation";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import ConversationTimeline from "@/conversation/components/ConversationTimeline.vue";
import CustomerCommunicationDialog from "@/customer/components/customer-communication/CustomerCommunicationDialog.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {usePatientStore} from "@/stores/patient.store";
import {computed, nextTick, watch} from "vue";

const props = defineProps<{
    customer: Customer
}>();

const patientStore = usePatientStore();
const {messages, isLoading, isLoadingMore, hasMore, load, loadMore} = useCustomerConversation(props.customer.id);

const isEmpty = computed(() => !isLoading.value && messages.value.length === 0);

/**
 * Scroll the conversation to the bottom to show the latest messages.
 */
const scrollToBottom = () => {
    nextTick(() => {
        const scrollable = document.querySelector('#application-layout-content');

        if (scrollable) {
            scrollable.scrollTop = scrollable.scrollHeight;
        }
    });
};

watch(isLoading, (loading) => {
    if (!loading && messages.value.length > 0) {
        scrollToBottom();
    }
});

const handleCertified = () => {
    return patientStore.refreshCustomer();
};

/**
 * When a message is sent, wait 1 second for the queue to process the message and then refresh the conversation
 */
const handleSent = () => {
    setTimeout(() => {
        load();
    }, 1000);
};
</script>

<template>
  <div class="flex-1">
    <div class="container mx-auto bg-white mt-4 rounded-lg">
      <BaseFlexSpinner v-if="isLoading" />
      <div v-else-if="isEmpty" class="p-4 text-sm text-gray-500 text-center flex flex-col gap-2">
        <p>Aucune conversation trouvée.</p>
        <CustomerCommunicationDialog :customer="customer" @certified="handleCertified" @sent="handleSent">
          <BaseButton density="comfortable" color="primary self-center">
            Envoyez un SMS
          </BaseButton>
        </CustomerCommunicationDialog>
      </div>
      <div v-else class="flex flex-col">
        <div class="flex justify-center p-2">
          <BaseButton
            v-if="hasMore()"
            :loading="isLoadingMore"
            density="comfortable"
            variant="text"
            @click="loadMore"
          >
            Charger les messages précédents
          </BaseButton>
        </div>
        <ConversationTimeline :messages="messages" />
      </div>
    </div>
  </div>
</template>
